import React from 'react'
import type { Block, Variable } from '../../types'
import { PropertyLabel, PropertyInput } from './shared'
import { Plus, Trash2, GripVertical } from 'lucide-react'

interface DropdownPropertiesProps {
  block: Block
  variable: Variable
  linkedBlocks: Block[]
  onUpdateBlock: (id: string, updates: Partial<Block>) => void
  onUpdateVariable: (name: string, updates: Partial<Variable>) => void
  onRenameVariable: (oldName: string, newName: string) => void
}

interface DropdownOption {
  label: string
  value: string
}

export default function DropdownProperties({
  block,
  variable,
  linkedBlocks,
  onUpdateBlock,
  onUpdateVariable,
  onRenameVariable,
}: DropdownPropertiesProps) {
  const config = (block.config || {}) as Record<string, unknown>
  const options = (config.options as DropdownOption[]) || []

  const updateOptions = (newOptions: DropdownOption[]) => {
    onUpdateBlock(block.id, { config: { ...config, options: newOptions } })
  }

  const updateOption = (index: number, field: 'label' | 'value', val: string) => {
    const newOptions = options.map((opt, i) =>
      i === index ? { ...opt, [field]: val } : opt
    )
    updateOptions(newOptions)
  }

  const addOption = () => {
    const num = options.length + 1
    updateOptions([...options, { label: `Option ${num}`, value: `option_${num}` }])
  }

  const removeOption = (index: number) => {
    if (options.length <= 1) return
    const removed = options[index]
    const newOptions = options.filter((_, i) => i !== index)
    updateOptions(newOptions)
    // If the removed option was the default, reset default to first remaining
    if (variable.default === removed.value) {
      onUpdateVariable(variable.name, { default: newOptions[0].value })
    }
  }

  const variant = (config.variant as string) || 'select'

  return (
    <>
      <div className="space-y-2">
        <PropertyLabel>Style</PropertyLabel>
        <div className="grid grid-cols-2 gap-1.5">
          {([
            { value: 'select', label: 'Select' },
            { value: 'radio', label: 'Radio' },
            { value: 'cards', label: 'Cards' },
            { value: 'chips', label: 'Chips' },
          ] as const).map((opt) => (
            <button
              key={opt.value}
              onClick={() => onUpdateBlock(block.id, { config: { ...config, variant: opt.value } })}
              className={`py-2 px-2 rounded-lg border text-xs font-medium transition-all ${
                variant === opt.value
                  ? 'border-[#5048e5] bg-[#5048e5]/5 text-[#5048e5]'
                  : 'border-slate-200 text-slate-500 hover:border-slate-300'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <PropertyLabel>Label</PropertyLabel>
        <PropertyInput
          value={block.label || ''}
          onChange={(e) => onUpdateBlock(block.id, { label: e.target.value })}
        />
      </div>

      <div className="space-y-2">
        <PropertyLabel>Linked Variable</PropertyLabel>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-[#5048e5] font-bold">@</span>
          <input
            type="text"
            value={variable.name}
            onChange={(e) =>
              onRenameVariable(variable.name, e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase())
            }
            className="w-full font-mono bg-slate-50 border border-slate-200 rounded-lg p-2.5 pl-7 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent transition-all outline-none"
          />
        </div>
      </div>

      <div className="space-y-2">
        <PropertyLabel>Default Value</PropertyLabel>
        <select
          value={String(variable.default)}
          onChange={(e) => onUpdateVariable(variable.name, { default: e.target.value })}
          className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent transition-all outline-none"
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <PropertyLabel>Options</PropertyLabel>
        <div className="space-y-2">
          {options.map((opt, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <GripVertical className="h-3.5 w-3.5 text-slate-300 shrink-0" />
              <input
                type="text"
                value={opt.label}
                onChange={(e) => updateOption(i, 'label', e.target.value)}
                placeholder="Label"
                className="flex-1 bg-slate-50 border border-slate-200 rounded-lg p-2 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent transition-all outline-none"
              />
              <input
                type="text"
                value={opt.value}
                onChange={(e) => updateOption(i, 'value', e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase())}
                placeholder="Value"
                className="w-24 font-mono bg-slate-50 border border-slate-200 rounded-lg p-2 text-xs focus:ring-2 focus:ring-[#5048e5] focus:border-transparent transition-all outline-none"
              />
              <button
                onClick={() => removeOption(i)}
                disabled={options.length <= 1}
                className="p-1.5 hover:bg-slate-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="Remove option"
              >
                <Trash2 className="h-3.5 w-3.5 text-slate-400" />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={addOption}
          className="w-full py-2 border border-dashed border-slate-300 text-slate-500 text-xs font-bold uppercase tracking-wider rounded-lg hover:border-[#5048e5] hover:text-[#5048e5] transition-all flex items-center justify-center gap-1.5"
        >
          <Plus className="h-3.5 w-3.5" />
          Add Option
        </button>
      </div>

      {linkedBlocks.length > 0 && (
        <div className="pt-4 border-t border-slate-100">
          <PropertyLabel>Linked Blocks</PropertyLabel>
          <ul className="space-y-1 mt-2">
            {linkedBlocks.map((lb) => (
              <li key={lb.id} className="text-sm text-slate-500">
                &bull; {lb.content?.slice(0, 30) || lb.label || lb.type}{' '}
                <span className="text-xs">({lb.type})</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}
